import {
  Box,
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { ReferralAnalytics } from "../../../types/referral";

interface StatCardProps {
  label: string;
  value: string | number;
}

const StatCard: React.FC<StatCardProps> = ({ label, value }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      <Typography variant="h4" fontWeight="bold">
        {value}
      </Typography>
    </CardContent>
  </Card>
);

interface ReferralAnalyticsDashboardProps {
  analytics: ReferralAnalytics;
}

const ReferralAnalyticsDashboard: React.FC<ReferralAnalyticsDashboardProps> = ({
  analytics,
}) => {
  const { t } = useTranslation();

  return (
    <Box>
      {/* Top row: 4 stat cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label={t("admin.referral.totalReferrals")}
            value={analytics.totalReferrals}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label={t("admin.referral.completedReferrals")}
            value={analytics.completedReferrals}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label={t("admin.referral.pendingReferrals")}
            value={analytics.pendingReferrals}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label={t("admin.referral.conversionRate")}
            value={`${analytics.conversionRate.toFixed(1)}%`}
          />
        </Grid>
      </Grid>

      {/* Second row: 2 more cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <StatCard
            label={t("admin.referral.rewardsIssued")}
            value={analytics.rewardsIssued}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <StatCard
            label={t("admin.referral.totalDiscountGiven")}
            value={`$${analytics.totalDiscountGiven.toFixed(2)}`}
          />
        </Grid>
      </Grid>

      {/* Top Referrers */}
      <Typography variant="h6" gutterBottom>
        {t("admin.referral.topReferrers")}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("admin.referral.name")}</TableCell>
              <TableCell>{t("admin.referral.email")}</TableCell>
              <TableCell align="right">{t("admin.referral.referralCount")}</TableCell>
              <TableCell align="right">{t("admin.referral.totalRewardsEarned")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {analytics.topReferrers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    {t("admin.referral.noTopReferrers")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              analytics.topReferrers.map((referrer) => (
                <TableRow key={referrer.userId} hover>
                  <TableCell>{referrer.userName}</TableCell>
                  <TableCell>{referrer.email}</TableCell>
                  <TableCell align="right">{referrer.referralCount}</TableCell>
                  <TableCell align="right">${referrer.totalRewardsEarned.toFixed(2)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ReferralAnalyticsDashboard;
